import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { theme } from 'styled-tools'
import CheckBox from '../../customer-form/checkbox'
import RadioButtonsOnlyLabel from '../../customer-form/radioButtonOnlyLabel'
import settings from '../../../components/settings'
import { FormOptions } from '../Options/FormOptions'

const QuestionPreamble = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  font-weight: ${(props) =>
    theme('quickQuestions.preamble.fontWeight')(props) || '300'};
  line-height: ${theme('quickQuestions.preamble.lineHeight')};
  margin: 8px 0px 8px 0px;
  color: ${theme(`quickQuestions.preamble.color`)};
  margin-top: 0;

  a {
    color: ${theme(`colors.link`)};
  }

  p {
    padding-top: 0;
    margin-top: 0;
    font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  }

  ul {
    padding: 0;
    margin: 0;
    margin-bottom: 14px;
    margin-top: 0;
  }
  ul > li {
    font-weight: 700;
    list-style-type: disc;
    list-style-position: inside;
    font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  }

  strong {
    font-weight: 700;
  }

  ${(props) => props.theme.media.min.sm`
    padding-right: 30px;
    font-size: ${theme('quickQuestions.preamble.fontSize.desktop')};

    p {
      font-size: ${theme('quickQuestions.preamble.fontSize.desktop')};
    }

    ul > li {
      font-size: ${theme('quickQuestions.preamble.fontSize.desktop')};
    }
  `};
`

const StyledFormWrapper = styled.div`
  ${(props) => props.theme.media.min.sm`
    display: ${theme(
      'questionBlock.textareaWrapper.displayBlock',
      'flex'
    )(props)};
    flex-direction: column;
  `};
  margin-bottom: 14px;

  & > div {
    margin-bottom: 0;
  }
  ${(props) =>
    props.theme.questionBlock?.textareaWrapper?.flexBasis
      ? css`
          flex-basis: ${theme('questionBlock.textareaWrapper.flexBasis')};
        `
      : ''}
`

const FormItemWrapper = styled.div`
  width: 100%;

  ${(props) => props.theme.media.min.sm`
    width: ${theme(
      'questionBlock.textareaWrapper.textArea.width',
      '370px'
    )(props)};
  `}
  textarea {
    border: 1px solid
      ${(props) =>
        theme('questionBlock.textArea.borderColor')(props) ||
        theme('forms.colors.border')(props)};
    border-radius: ${theme('questionBlock.textArea.borderRadius')};
    &:focus {
      outline: none;
      border: 1px solid ${theme('forms.colors.activeBorder')};
      box-shadow: ${theme('colors.accentShadow')};
    }
  }
`

const LabelOnlyRadioGroup = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 20px !important;
`

const LabelOnlyItemWrapper = styled.div`
  width: 100%;
  text-align: center;
  ${(props) => props.theme.media.min.sm`
    width: auto;
  `};
`

export const FormQuestion = ({ question, answers, onChange }) => {
  const [value, setValue] = useState({})

  const isFormCheckBox = useMemo(
    () => question.optionType === 'form-checkbox',
    [question.optionType]
  )
  const isFormCheckboxMagnet = useMemo(
    () => isFormCheckBox && settings.brand === 'magnet',
    [isFormCheckBox]
  )
  const isFormCheckboxNotMagnet = useMemo(
    () => isFormCheckBox && settings.brand !== 'magnet',
    [isFormCheckBox]
  )

  const showForm = useMemo(
    () => value.checkbox === question.otherAnswerText,
    [value.checkbox, question.otherAnswerText]
  )

  useEffect(() => {
    try {
      setValue(JSON.parse(answers[0] || '{}'))
    } catch (error) {
      setValue({})
    }
  }, [answers])

  const update = useCallback(
    (form) => {
      const empty = Object.keys(form).length === 0
      const updated = JSON.stringify(form)
      if (updated !== answers[0]) {
        onChange(empty ? [] : [updated])
      }
    },
    [onChange]
  )

  const onChangeFormData = useCallback(
    (values) => {
      setValue(({ checkbox }) => ({ checkbox, ...values }))
    },
    [value]
  )

  const onChangeCheckbox = useCallback(
    (e) => {
      const v = { checkbox: e.target.value }
      setValue(v)
      update(v)
    },
    [update]
  )

  const onBlur = useCallback(() => {
    update(value)
  }, [update, value])

  return (
    <StyledFormWrapper>
      {isFormCheckboxNotMagnet && (
        <FormItemWrapper>
          <CheckBox
            label={question.otherAnswerText}
            checked={value.checkbox === question.otherAnswerText}
            name="checkbox"
            value={
              value.checkbox === question.otherAnswerText
                ? ''
                : question.otherAnswerText
            }
            onChange={onChangeCheckbox}
          />
        </FormItemWrapper>
      )}
      {isFormCheckboxMagnet && (
        <LabelOnlyRadioGroup>
          {question.options.map(
            (o) =>
              o.contentType === 'TextBoxedOption' && (
                <LabelOnlyItemWrapper
                  data-selected={value.checkbox === o.firstText}
                  data-track={`option-${o.name}`.toLowerCase()}
                  key={o.name}
                >
                  <RadioButtonsOnlyLabel
                    name="checkbox"
                    value={o.firstText}
                    label={o.firstText}
                    checked={value.checkbox === o.firstText}
                    onChange={onChangeCheckbox}
                  />
                </LabelOnlyItemWrapper>
              )
          )}
          {question.otherAnswerText && (
            <LabelOnlyItemWrapper
              data-selected={value.checkbox === question.otherAnswerText}
              data-track={`option-${question.otherAnswerText}`.toLowerCase()}
            >
              <RadioButtonsOnlyLabel
                name="checkbox"
                value={
                  value.checkbox === question.otherAnswerText
                    ? ''
                    : question.otherAnswerText
                }
                label={question.otherAnswerText}
                checked={value.checkbox === question.otherAnswerText}
                onChange={onChangeCheckbox}
              />
            </LabelOnlyItemWrapper>
          )}
        </LabelOnlyRadioGroup>
      )}
      {showForm &&
        settings.brand === 'magnet' &&
        question.questionSupportingText && (
          <QuestionPreamble style={{ marginBottom: '15px', fontSize: '16px' }}>
            {question.questionSupportingText}
          </QuestionPreamble>
        )}
      {showForm && (
        <FormOptions
          options={question.options || []}
          values={value}
          onChange={onChangeFormData}
          onBlur={onBlur}
        />
      )}
    </StyledFormWrapper>
  )
}
